/**
 * Homie Readiness Quiz — question definitions, answer→score mapping, and the
 * pre-signup browser buffer.
 *
 * The score itself is NOT a second formula: answers are mapped to the same
 * `ReadinessInputs` the published /tools/homebuyer-readiness-score tool uses,
 * so the number a visitor sees here matches the number that tool produces.
 */
import {
  calculateReadiness,
  type ReadinessInputs,
  type ReadinessResult,
} from "@/components/tools/readinessScoreData";

export const QUIZ_BUFFER_KEY = "ha_homie_quiz";
export const QUIZ_STEP_COUNT = 6;

export type TimelineValue = "0-6" | "6-12" | "12-24" | "exploring";
export type SavingsValue = "none" | "under5k" | "5k-15k" | "15k-40k" | "40k+";
export type CreditValue = "excellent" | "good" | "fair" | "poor" | "unsure";

export interface QuizAnswers {
  timeline?: TimelineValue;
  savings?: SavingsValue;
  credit?: CreditValue;
  annualIncome?: number;
  monthlyDebt?: number;
  occupation?: string;
  employerName?: string;
  zip?: string;
}

export interface Choice<T extends string> {
  value: T;
  label: string;
}

export const TIMELINE_CHOICES: Choice<TimelineValue>[] = [
  { value: "0-6", label: "In the next 6 months" },
  { value: "6-12", label: "6 to 12 months from now" },
  { value: "12-24", label: "1 to 2 years out" },
  { value: "exploring", label: "Just exploring for now" },
];

export const SAVINGS_CHOICES: Choice<SavingsValue>[] = [
  { value: "none", label: "Haven't started yet" },
  { value: "under5k", label: "Under $5,000" },
  { value: "5k-15k", label: "$5,000 – $15,000" },
  { value: "15k-40k", label: "$15,000 – $40,000" },
  { value: "40k+", label: "$40,000 or more" },
];

export const CREDIT_CHOICES: Choice<CreditValue>[] = [
  { value: "excellent", label: "Excellent — 740 or higher" },
  { value: "good", label: "Good — 670 to 739" },
  { value: "fair", label: "Fair — 580 to 669" },
  { value: "poor", label: "Rough — under 580" },
  { value: "unsure", label: "Honestly, I'm not sure" },
];

/**
 * Occupations that map onto the existing Hero Stack categories. "Other" is
 * intentionally last and does not set a hero category.
 */
export const OCCUPATION_CHOICES: { value: string; label: string; heroCategory: string | null }[] = [
  { value: "educator", label: "Teacher / educator", heroCategory: "Educator" },
  { value: "healthcare", label: "Nurse / healthcare", heroCategory: "Healthcare Provider" },
  { value: "first_responder", label: "Firefighter / EMS", heroCategory: "Firefighter" },
  { value: "law_enforcement", label: "Law enforcement", heroCategory: "Law Enforcement" },
  { value: "military", label: "Military / veteran", heroCategory: "Military / Veteran" },
  { value: "government", label: "Government employee", heroCategory: "Government Employee" },
  { value: "nonprofit", label: "Non-profit", heroCategory: "Non-Profit" },
  { value: "other", label: "None of these", heroCategory: null },
];

export const heroCategoryFor = (occupation?: string): string | null =>
  OCCUPATION_CHOICES.find((o) => o.value === occupation)?.heroCategory ?? null;

const SAVINGS_MIDPOINT: Record<SavingsValue, number> = {
  none: 0,
  under5k: 2500,
  "5k-15k": 10000,
  "15k-40k": 27500,
  "40k+": 55000,
};

const CREDIT_MIDPOINT: Record<CreditValue, number> = {
  excellent: 760,
  good: 700,
  fair: 625,
  poor: 550,
  // Conservative-but-fair placeholder; results copy tells them to refine it.
  unsure: 660,
};

const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));

/**
 * Maps quiz ranges onto the shared readiness inputs.
 *
 * Two inputs the quiz deliberately does not ask about (to keep it to six
 * screens) get neutral assumptions: no recent derogatory events, and two
 * years of employment. Both are stated as assumptions on the results screen.
 */
export function answersToInputs(a: QuizAnswers): ReadinessInputs {
  const annualIncome = a.annualIncome && a.annualIncome > 0 ? a.annualIncome : 60000;
  const monthlyIncome = annualIncome / 12;
  const monthlyDebt = a.monthlyDebt && a.monthlyDebt > 0 ? a.monthlyDebt : 0;
  const savings = SAVINGS_MIDPOINT[a.savings ?? "none"];

  // Rough target price so savings can be expressed as a percent: a common
  // affordability rule of thumb is roughly 3.5x gross annual income.
  const targetPrice = Math.max(120000, annualIncome * 3.5);
  const downPaymentPercent = clamp((savings / targetPrice) * 100, 0, 20);

  // Whatever is left after a 3% down payment, measured in months of expenses.
  const monthlyBurn = Math.max(1200, monthlyIncome * 0.45 + monthlyDebt);
  const leftover = Math.max(0, savings - targetPrice * 0.03);
  const reserveMonths = clamp(leftover / monthlyBurn, 0, 6);

  return {
    creditScore: CREDIT_MIDPOINT[a.credit ?? "unsure"],
    monthlyIncome,
    monthlyDebt,
    downPaymentPercent: Math.round(downPaymentPercent * 10) / 10,
    reserveMonths: Math.round(reserveMonths * 10) / 10,
    employmentYears: 2,
    derogatory: "none",
  };
}

export type QuizTier = "Mortgage Ready" | "Ready to Buy" | "Getting There";

export const tierFor = (score: number): QuizTier =>
  score >= 80 ? "Mortgage Ready" : score >= 60 ? "Ready to Buy" : "Getting There";

export interface QuizOutcome {
  score: number;
  tier: QuizTier;
  result: ReadinessResult;
  /** Homie's plain-language take, 2–3 sentences. */
  take: string;
  /** The single weakest factor, by share of its available weight. */
  weakest: ReadinessResult["factors"][number];
  nextSteps: { title: string; body: string }[];
}

const TIER_OPENER: Record<QuizTier, string> = {
  "Mortgage Ready":
    "Good news — on paper you look like someone a lender is ready to work with today.",
  "Ready to Buy":
    "You're close. Most of your profile is in decent shape, with one thing dragging the rest down.",
  "Getting There":
    "You're earlier in this than you might like to hear, and that's genuinely fine — it just means a plan instead of a purchase right now.",
};

const WEAKEST_FIX: Record<string, string> = {
  credit:
    "Your credit is the piece holding the score back the most. Getting each card below about 10% of its limit before the statement closes is usually the fastest honest lift.",
  dti: "Your monthly debt load relative to income is the biggest drag. Clearing the smallest installment loan removes its whole payment from the math, which moves faster than shaving balances.",
  cash: "Cash on hand is the main gap. Before you assume you need to save more, check what down payment help you qualify for — that is exactly what your Stack is for.",
  history:
    "Your credit history has a mark on it that lenders weigh heavily. Time is the main cure; keep everything current in the meantime.",
  employment:
    "Income documentation is the weak spot. Staying in the same job or field until closing is worth more here than a raise somewhere new.",
};

export function buildOutcome(answers: QuizAnswers): QuizOutcome {
  const result = calculateReadiness(answersToInputs(answers));
  const tier = tierFor(result.score);

  const weakest = [...result.factors].sort(
    (a, b) => a.points / a.weight - b.points / b.weight,
  )[0];

  const take = `${TIER_OPENER[tier]} ${WEAKEST_FIX[weakest.id] ?? weakest.tip}`;

  const nextSteps: { title: string; body: string }[] = [
    {
      title: "Down payment help",
      body: "See which federal, state, county, and employer programs you may line up for in your area.",
    },
  ];
  if (weakest.id === "credit" || weakest.id === "history") {
    nextSteps.push({
      title: "Credit building",
      body: "Work the specific items lenders score, in the order that moves the number fastest.",
    });
  }
  if (weakest.id === "dti") {
    nextSteps.push({
      title: "Debt paydown plan",
      body: "Target the debts that change your ratio most, not just the biggest balances.",
    });
  }
  nextSteps.push({
    title: "Talk it through",
    body: "When you're ready, we can connect you with an independent professional to review your situation.",
  });

  return { score: result.score, tier, result, take, weakest, nextSteps };
}

/* ---------------------------------------------------------------------- */
/* Pre-signup browser buffer                                               */
/* ---------------------------------------------------------------------- */

export interface QuizBuffer {
  answers: QuizAnswers;
  score: number;
  tier: QuizTier;
  band: string;
  heroCategory: string | null;
  completedAt: string;
}

export const readQuizBuffer = (): QuizBuffer | null => {
  try {
    const raw = localStorage.getItem(QUIZ_BUFFER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as QuizBuffer;
    return parsed && typeof parsed.score === "number" ? parsed : null;
  } catch {
    return null;
  }
};

export const writeQuizBuffer = (buffer: QuizBuffer) => {
  try {
    localStorage.setItem(QUIZ_BUFFER_KEY, JSON.stringify(buffer));
  } catch {
    /* storage unavailable — the score still shows on screen */
  }
};

export const clearQuizBuffer = () => {
  try {
    localStorage.removeItem(QUIZ_BUFFER_KEY);
  } catch {
    /* no-op */
  }
};

/** Signup destination that brings them straight back to their results. */
export const quizSignupPath = (zip?: string | null) => {
  const returnTo = `/homie?saved=1${zip ? `&zip=${zip}` : ""}`;
  return `/auth?returnTo=${encodeURIComponent(returnTo)}&signup_only=true&source=homie-quiz&track=homebuyer`;
};

/** Members are nudged to retake once a month. */
export const RETAKE_INTERVAL_DAYS = 30;

export const daysSince = (iso: string) =>
  Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
